import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CreditCard, ExternalLink, AlertTriangle, CheckCircle2, RotateCw } from "lucide-react";
import api from "@/lib/api";

/**
 * Stripe Connect Express status + onboarding panel.
 *
 * Three render states:
 *   1. configuration_required  → admin needs to set STRIPE_PLATFORM_SECRET_KEY
 *   2. !connected              → "Connect Stripe" button → hits /onboard → redirects
 *   3. connected               → status pills + dashboard link + disconnect
 *
 * Designed to slot into Settings → Integrations.
 */
export default function StripeConnectPanel({ workspaceId, returnPath = "/app/settings?tab=integrations" }) {
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const r = await api.get(`/integrations/stripe/status`, { params: { workspace_id: workspaceId } });
      setStatus(r.data);
    } catch {
      setStatus({ configured: false, configuration_required: true, connected: false, status: "disconnected" });
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    load();
    const flag = params.get("stripe");
    if (flag === "connected") {
      toast.success("Stripe onboarding complete — checking status…");
      params.delete("stripe");
      setParams(params, { replace: true });
    } else if (flag === "expired") {
      toast.error("Stripe onboarding session expired — please try again");
      params.delete("stripe");
      setParams(params, { replace: true });
    } else if (flag === "error") {
      toast.error("Stripe onboarding failed — please try again");
      params.delete("stripe");
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  const startOnboarding = async () => {
    if (!workspaceId) return;
    setBusy(true);
    try {
      const r = await api.post(`/integrations/stripe/onboard`, {
        workspace_id: workspaceId,
        return_path: returnPath,
      });
      window.location.href = r.data.onboarding_url;
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not start Stripe onboarding";
      toast.error(detail);
      setBusy(false);
    }
  };

  const openDashboard = async () => {
    if (!workspaceId) return;
    setBusy(true);
    try {
      const r = await api.post(`/integrations/stripe/login-link`, { workspace_id: workspaceId });
      window.open(r.data.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open Stripe dashboard");
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!workspaceId) return;
    if (!window.confirm("Disconnect Stripe? Future bookings will not collect deposits until you reconnect.")) return;
    try {
      await api.delete(`/integrations/stripe`, { params: { workspace_id: workspaceId } });
      toast.success("Stripe disconnected");
      load();
    } catch {
      toast.error("Could not disconnect");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-between gap-3 bg-[#f5efe6] p-4 rounded-lg" data-testid="integration-stripe-loading">
        <div className="flex items-center gap-3 min-w-0">
          <CreditCard className="w-5 h-5 text-[#005e7a]" />
          <div className="min-w-0">
            <p className="font-medium">Stripe (payments)</p>
            <p className="text-xs text-[#82828c]">Checking status…</p>
          </div>
        </div>
      </div>
    );
  }

  const { connected, status: connStatus,
    charges_enabled, payouts_enabled, details_submitted, country, default_currency,
    email: connectedEmail, requirements_currently_due, requirements_disabled_reason } = status || {};

  return (
    <div className="bg-[#f5efe6] p-4 rounded-lg flex flex-col gap-3" data-testid="integration-stripe">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <CreditCard className="w-5 h-5 text-[#005e7a] flex-shrink-0" />
          <div className="min-w-0">
            <p className="font-medium">Stripe (payments)</p>
            <p className="text-xs text-[#82828c]">
              Charge for bookings — deposits, full payment, and invoices via your own Stripe account.
            </p>
            {connected && connectedEmail && (
              <p className="text-xs text-[#005e7a] mt-1 font-medium">
                Connected as {connectedEmail}{country ? ` · ${country}` : ""}{default_currency ? ` · ${default_currency.toUpperCase()}` : ""}
              </p>
            )}
          </div>
        </div>
        <div className="flex-shrink-0 flex items-center gap-2">
          {!connected ? (
            <span
              className="bloq-pill"
              style={{ background: "rgba(0,94,122,0.12)", color: "#005e7a" }}
              data-testid="stripe-coming-soon-pill"
            >
              Coming soon
            </span>
          ) : (
            <>
              <button
                onClick={openDashboard}
                disabled={busy}
                className="bloq-btn-secondary text-xs px-3 py-1.5 inline-flex items-center gap-1 disabled:opacity-60"
                data-testid="stripe-dashboard-button"
              >
                <ExternalLink className="w-3 h-3" /> Dashboard
              </button>
              <button
                onClick={load}
                disabled={busy}
                className="bloq-btn-secondary text-xs px-2 py-1.5 inline-flex items-center disabled:opacity-60"
                title="Refresh status"
                data-testid="stripe-refresh-button"
              >
                <RotateCw className="w-3 h-3" />
              </button>
              <button
                onClick={disconnect}
                className="bloq-btn-secondary text-xs px-3 py-1.5"
                data-testid="stripe-disconnect-button"
              >
                Disconnect
              </button>
            </>
          )}
        </div>
      </div>

      {connected && (
        <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="stripe-status-pills">
          <StatusPill label="Charges" ok={charges_enabled} />
          <StatusPill label="Payouts" ok={payouts_enabled} />
          <StatusPill label="Details submitted" ok={details_submitted} />
          {connStatus === "restricted" && (
            <span className="bloq-pill" style={{ background: "rgba(180,83,9,0.15)", color: "#b45309" }}>
              Restricted
            </span>
          )}
        </div>
      )}

      {connected && requirements_currently_due && requirements_currently_due.length > 0 && (
        <div
          className="text-xs text-[#b45309] bg-[#fef3c7] p-3 rounded-md border border-[#fbbf24]"
          data-testid="stripe-requirements-banner"
        >
          <p className="font-medium flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" /> Stripe needs more information from you
          </p>
          <p className="mt-1">{requirements_currently_due.length} item(s) outstanding. Click <strong>Connect Stripe</strong> again to finish onboarding.</p>
          {requirements_disabled_reason && (
            <p className="mt-1 italic">Reason: {requirements_disabled_reason}</p>
          )}
          <button
            onClick={startOnboarding}
            disabled={busy}
            className="bloq-btn-primary text-xs px-3 py-1.5 mt-2"
            data-testid="stripe-finish-onboarding-button"
          >
            {busy ? "Redirecting…" : "Finish onboarding"}
          </button>
        </div>
      )}
    </div>
  );
}

function StatusPill({ label, ok }) {
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-1 rounded-full font-medium"
      style={{
        background: ok ? "rgba(34,197,94,0.15)" : "rgba(180,83,9,0.15)",
        color: ok ? "#15803d" : "#b45309",
      }}
      data-testid={`stripe-pill-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      {ok ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
      {label} {ok ? "enabled" : "pending"}
    </span>
  );
}
